/**
 * "Photos" gallery card on the vendor profile — the tagged photos customers
 * see on the vendor's page. Backed by /vendor/photos (max 5 per tag).
 */
import { useState } from 'react';
import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { AuthImage, open_file_in_new_tab } from '@/components/auth-image';
import { form_error_message } from '@/lib/forms';
import { PHOTO_TYPE_LABELS } from '@/features/onboarding/types';
import {
  use_delete_gallery_photo,
  use_gallery_photos,
  use_upload_gallery_photo,
} from '@/features/vendor-gallery/hooks';
import {
  ALLOWED_PHOTO_MIME,
  GALLERY_PHOTO_TYPES,
  MAX_PHOTO_BYTES,
  MAX_PHOTOS_PER_TAG,
  photo_variant_path,
  type GalleryPhoto,
  type GalleryPhotoType,
} from '@/features/vendor-gallery/types';

const TAG_OPTIONS = GALLERY_PHOTO_TYPES.map((t) => ({ value: t, label: PHOTO_TYPE_LABELS[t] }));

export function GalleryPhotosCard() {
  const photos_q = use_gallery_photos();
  const upload = use_upload_gallery_photo();
  const remove = use_delete_gallery_photo();

  const [tag, set_tag] = useState<GalleryPhotoType>('STORE_FRONT');
  const [error, set_error] = useState<string | null>(null);
  const [progress, set_progress] = useState<{ done: number; total: number } | null>(null);
  const [deleting_id, set_deleting_id] = useState<string | null>(null);

  const photos = photos_q.data ?? [];
  const count_for = (t: GalleryPhotoType) => photos.filter((p) => p.type === t).length;
  const tag_count = count_for(tag);
  const room = Math.max(0, MAX_PHOTOS_PER_TAG - tag_count);
  const busy = progress !== null;

  async function pick(input: HTMLInputElement) {
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) return;
    set_error(null);

    const label = PHOTO_TYPE_LABELS[tag];
    if (files.length > room) {
      return set_error(
        room === 0
          ? `${label} already has ${MAX_PHOTOS_PER_TAG} photos. Remove one to add another.`
          : `${label} has room for ${room} more photo${room === 1 ? '' : 's'} — you picked ${files.length}.`,
      );
    }
    const bad_type = files.find((f) => !ALLOWED_PHOTO_MIME.includes(f.type));
    if (bad_type) return set_error(`${bad_type.name}: only JPEG, PNG or WEBP images are allowed.`);
    const too_big = files.find((f) => f.size > MAX_PHOTO_BYTES);
    if (too_big) return set_error(`${too_big.name} is larger than 5 MB.`);

    set_progress({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      try {
        await upload.mutateAsync({ type: tag, file: files[i] });
        set_progress({ done: i + 1, total: files.length });
      } catch (err) {
        const msg = form_error_message(err, `Could not upload ${files[i].name}.`);
        set_error(i > 0 ? `${msg} (${i} of ${files.length} uploaded.)` : msg);
        break;
      }
    }
    set_progress(null);
  }

  async function delete_photo(photo: GalleryPhoto) {
    if (!window.confirm(`Remove this ${PHOTO_TYPE_LABELS[photo.type].toLowerCase()} photo?`)) return;
    set_error(null);
    set_deleting_id(photo.id);
    try {
      await remove.mutateAsync(photo.id);
    } catch (err) {
      set_error(form_error_message(err, 'Could not remove the photo.'));
    } finally {
      set_deleting_id(null);
    }
  }

  const groups = GALLERY_PHOTO_TYPES.map((t) => ({
    type: t,
    items: photos.filter((p) => p.type === t).sort((a, b) => a.display_order - b.display_order),
  })).filter((g) => g.items.length > 0);

  return (
    <Card className="lg:col-span-2" data-testid="profile-gallery-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Photos</CardTitle>
        <p className="text-sm text-muted-foreground">
          Photos customers see on your page. Pick a tag and add up to 5 photos per tag.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-col gap-1.5 sm:w-64">
            <Label htmlFor="gallery-tag">Tag</Label>
            <SelectField
              id="gallery-tag"
              data-testid="gallery-tag"
              value={tag}
              onValueChange={(v) => {
                set_tag(v as GalleryPhotoType);
                set_error(null);
              }}
              options={TAG_OPTIONS}
              disabled={busy}
            />
          </div>
          <p className="text-sm font-medium text-muted-foreground sm:pb-2.5" data-testid="gallery-tag-count">
            ({tag_count}/{MAX_PHOTOS_PER_TAG})
          </p>
          <label className="w-fit">
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="gallery-input"
              disabled={busy || room === 0 || photos_q.isLoading}
              onChange={(e) => void pick(e.target)}
            />
            <Button
              asChild
              variant="secondary"
              size="sm"
              className="gap-2"
              disabled={busy || room === 0 || photos_q.isLoading}
            >
              <span data-testid="gallery-upload" aria-disabled={busy || room === 0}>
                {progress ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress.done + 1} of{' '}
                    {progress.total}…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" /> Add photos
                  </>
                )}
              </span>
            </Button>
          </label>
        </div>
        <p className="-mt-3 text-xs text-muted-foreground">JPEG, PNG or WEBP, up to 5 MB each.</p>

        {error && (
          <p className="text-sm text-destructive" role="alert" data-testid="gallery-error">
            {error}
          </p>
        )}

        {photos_q.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading photos…
          </div>
        ) : photos_q.isError ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-destructive">
              {form_error_message(photos_q.error, "Couldn't load your photos.")}
            </p>
            <Button variant="secondary" size="sm" onClick={() => photos_q.refetch()}>
              Retry
            </Button>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ImageIcon className="h-5 w-5" /> No photos yet.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {groups.map((g) => (
              <div key={g.type} data-testid={`gallery-group-${g.type}`}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {PHOTO_TYPE_LABELS[g.type]} ({g.items.length}/{MAX_PHOTOS_PER_TAG})
                </p>
                <div className="flex flex-wrap gap-3">
                  {g.items.map((p) => (
                    <div
                      key={p.id}
                      className="relative h-24 w-24 overflow-hidden rounded-card border-2 border-border bg-secondary"
                    >
                      <button
                        type="button"
                        className="h-full w-full"
                        title={p.original_filename}
                        onClick={() => void open_file_in_new_tab(photo_variant_path(p.file_path, 'full'))}
                      >
                        <AuthImage
                          path={photo_variant_path(p.file_path, 'thumb')}
                          alt={p.original_filename}
                          className="h-full w-full object-cover"
                          data-testid={`gallery-photo-${p.id}`}
                        />
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${p.original_filename}`}
                        data-testid={`gallery-remove-${p.id}`}
                        disabled={deleting_id === p.id}
                        onClick={() => void delete_photo(p)}
                        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-card text-destructive shadow-sm hover:bg-secondary disabled:opacity-40"
                      >
                        {deleting_id === p.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
